import { Skeleton, Tooltip } from "antd";

export function formatCurrencyNoTooltip(value, formatType = "absolute") {
  const num = Number(value);

  if (isNaN(num)) return "₹0";

  switch (formatType.toLowerCase()) {
    case "lakh":
      return `₹${(num / 1e5).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })} L`;

    case "crore":
      return `₹${(num / 1e7).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })} Cr`;

    case "absolute":
    default:
      return `₹${num.toLocaleString("en-IN")}`;
  }
}

export function formatCurrency(value, formatType = "absolute") {
  const num = Number(value);

  if (isNaN(num)) return "₹0";

  switch (formatType.toLowerCase()) {
    case "lakh":
      return (
        <Tooltip title={`₹${num.toLocaleString("en-IN")}`}>{`₹${(
          num / 1e5
        ).toLocaleString("en-IN", { minimumFractionDigits: 2 })} L`}</Tooltip>
      );

    case "crore":
      const croreValue = Number((num / 1e7).toFixed(2));
      return (
        <Tooltip title={`₹${num.toLocaleString("en-IN")}`}>
          {`₹${croreValue.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} Cr`}
        </Tooltip>
      );

    case "absolute":
    default:
      return (
        <Tooltip title={`₹${num.toLocaleString("en-IN")}`}>
          {`₹${num.toLocaleString("en-IN")}`}
        </Tooltip>
      );
  }
}
export function formatPrice(value, locale = "en-IN", currency = "INR") {
  const absValue = Math.abs(value);
  let formattedValue;
  let suffix = "";

  if (absValue >= 1_000_000_000_000) {
    formattedValue = (value / 1_000_000_000_000).toFixed(2);
    suffix = "T";
  } else if (absValue >= 1_000_000_000) {
    formattedValue = (value / 1_000_000_000).toFixed(2);
    suffix = "B";
  } else if (absValue >= 1_000_000) {
    formattedValue = (value / 1_000_000).toFixed(2);
    suffix = "M";
  } else if (absValue >= 1_000) {
    formattedValue = (value / 1_000).toFixed(2);
    suffix = "K";
  } else {
    formattedValue = value.toFixed(2);
  }

  // Remove trailing .00 if not needed
  if (formattedValue.endsWith(".00")) {
    formattedValue = formattedValue.slice(0, -3);
  }

  // Get the currency symbol for INR (₹)
  const currencySymbol = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(0)
    .replace(/[\d.,\s]/g, "");

  return `${currencySymbol}${formattedValue}${suffix}`;
}

export function transformChartData(data, suffix = "_count") {
  if (!data || !Array.isArray(data)) return { data: [], keys: [], colors: [] };

  // Full abbreviation map
  const labelOverrides = {
    Broken_PTP: "BPTP",
    Busy_Engaged: "BE",
    Call_Back: "CB",
    Deceased: "DCD",
    Disconnected: "DC",
    Dispute: "DSP",
    Follow_up: "FU",
    Future_PTP: "FPTP",
    Invalid_Mob_No: "Invalid No",
    Language_Barrier: "Lang Barrier",
    Mob_No_Not_Available: "No Mob",
    No_Proper_Address: "No Addr",
    Not_Reachable: "NR",
    Paid: "Paid",
    Part_payment_Made: "PPM",
    Promise_To_Pay: "PTP",
    Refuse_To_Pay: "RTP",
    Revisit: "RV",
    Ringing: "Ringing",
    Settlement_Under_Process: "Settlement",
    Shifted_to_other_Place: "Shifted",
    Switch_Off: "Switched Off",
    Third_Party_Left_Message: "TPLM",
    Untouched: "Untouched",
    Wrong_Number: "Wrong No",
  };

  // Predefined color palette
  const predefinedColors = [
    "#4CAF50",
    "#2196F3",
    "#FFC107",
    "#FF5722",
    "#9C27B0",
    "#00BCD4",
    "#E91E63",
    "#3F51B5",
    "#FF9800",
    "#795548",
    "#607D8B",
    "#8BC34A",
    "#CDDC39",
    "#009688",
    "#673AB7",
    "#F44336",
    "#FFEB3B",
    "#03A9F4",
    "#B71C1C",
    "#004D40",
    "#33691E",
    "#1A237E",
    "#263238",
    "#6D4C41",
    "#D32F2F",
  ];

  // Extract keys ending in _suffix
  const rawKeys = Object.keys(data[0] || {}).filter((key) =>
    key.endsWith(suffix)
  );

  // Generate short labels
  const keyMap = Object.fromEntries(
    rawKeys.map((fullKey) => {
      const baseKey = fullKey.replace(suffix, "");
      const label =
        labelOverrides[baseKey] ||
        baseKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return [fullKey, label];
    })
  );

  const shortKeys = Object.values(keyMap);

  // Transform each data entry
  const transformedData = data.map((entry) => {
    const result = { name: entry.name };
    for (const [longKey, shortKey] of Object.entries(keyMap)) {
      if (entry[longKey] !== undefined) {
        result[shortKey] = entry[longKey];
      }
    }
    return result;
  });

  // Use predefined colors (loop if there are more keys than colors)
  const colors = shortKeys.map(
    (_, index) => predefinedColors[index % predefinedColors.length]
  );

  return { data: transformedData, keys: shortKeys, colors };
}

export function formatWithCommas(number, locale = "en-US") {
  if (isNaN(number)) {
    return 0;
  }
  return new Intl.NumberFormat(locale).format(number);
}



export const getUserType = (user) => {
  // return "superuser";
  if (!user || !user.idToken) return;

  if (user.selectedRole) return user.selectedRole;

  if (user.is_supervisor) return "supervisor";
  if (user.is_superuser) return "superuser";
  if (user.is_tl) return "team_lead";
  if (user.is_tc) return "telecaller";
  if (user.is_fa) return "field_agent";
  if (user.is_agency) return "agency";

  return "agency";
};

export const MONTH_NAME_TO_ID = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

export const normalizeMonthIds = (months) => {
  if (!months) return "";
  const arr = Array.isArray(months) ? months : [months];
  return arr
    .map((m) => {
      if (typeof m === "string") {
        const lower = m.toLowerCase().trim();
        if (MONTH_NAME_TO_ID[lower]) return MONTH_NAME_TO_ID[lower];
      }
      return m;
    })
    .filter(Boolean)
    .join(",");
};

